import { TableCellProps, TextFieldProps, Tooltip } from '@mui/material'

import { TimeSeriesSettingValue } from 'api/digitalTwin/digitalTwin.api'
import clsx from 'clsx'
import React, { ReactNode } from 'react'
import SelectButton from 'ui/components/SelectButton/SelectButton'
import { TablePropsFactoryFnProps } from 'ui/components/TimeSeriesDataTable/types'
import DatasetTableHeader from 'ui/uiConfig/components/DatasetTable/DatasetTableHeader'

import getRowIndex from '../TimeSeriesDataTable/utils/getRowIndex'

import { getSettingData } from './TimeSeriesSettingTable'
import styles from './TimeSeriesSettingTable.module.less'
import {
  fillHandleMarkerProps,
  getArrowKeyFocusTarget,
  getRowIndexRange,
  getSettingValuesFromPaste,
  getSettingValuesWithValueAtRows,
  isFillHandleTarget,
} from './timeSeriesSettingTableEdit.helper'
import {
  CreateHeaderOptions,
  CreateMUITableBodyCellEditTextFieldPropsOptions,
  CreateMUITableBodyCellPropsOptions,
  EditableColumnFactory,
  FactoryDependencies,
} from './types'

export default function createEditableColumnFactory({
  datasets,
  chartItems,
  invalidValuesIndex,
  selectedRows,
  activeEdit,
  mouseDownValue,
  mouseOverIndex,
  focusedCell,
  times,
  settingsData,
  t,
  copyDatasetToSettingValues,
  handleMouseEvent,
  setActiveEdit,
  setFocusedCell,
  setMouseOverIndex,
  setMouseDownValue,
  setSelectedRows,
  selectRowByClick,
  updateTimeSeriesValues,
  setSettingData,
  cellRefs,
  disabled,
}: FactoryDependencies): EditableColumnFactory {
  return {
    Header: ({ title, returnId, unit, index = 0 }: CreateHeaderOptions) => {
      return (): ReactNode => (
        <>
          <DatasetTableHeader title={title} unit={unit} />
          {datasets.filter((dataset) => dataset.return_id !== returnId).length > 0 && (
            <div className={styles.TimeSeriesSettingTable_CopyFromButtonContainer}>
              <SelectButton
                icon={'fal fa-copy'}
                link
                noPadding
                disabled={disabled}
                menuItems={
                  datasets
                    .filter((dataset) => dataset.return_id !== returnId)
                    .map((dataset) => ({
                      label: chartItems.find((item) => item.data_id === dataset.return_id)?.title,
                      onClick: () => copyDatasetToSettingValues(dataset, index, selectedRows),
                    }))
                    .filter((item) => item.label !== undefined) as { label: string; onClick: () => void }[]
                }
              >
                {t('Copy from')}
              </SelectButton>
            </div>
          )}
        </>
      )
    },
    muiTableBodyCellProps: ({ index }: CreateMUITableBodyCellPropsOptions) => {
      return ({ row, table, column }: TablePropsFactoryFnProps): TableCellProps => ({
        className: clsx(
          !disabled && styles.TimeSeriesSettingTable_FillableCell,
          invalidValuesIndex?.includes(getRowIndex(row, table.getState().pagination))
            ? styles.TimeSeriesSettingTable_InvalidInput
            : mouseDownValue !== null &&
                mouseOverIndex !== null &&
                row.index >= Math.min(mouseDownValue.rowIndex, mouseOverIndex) &&
                row.index <= Math.max(mouseDownValue.rowIndex, mouseOverIndex)
              ? styles.TimeSeriesSettingTable_HighlighedInput
              : selectedRows.includes(getRowIndex(row, table.getState().pagination))
                ? styles.TimeSeriesSettingTable_SelectedInput
                : undefined
        ),
        onKeyDown: (event: React.KeyboardEvent<HTMLTableCellElement>) => {
          const { pageIndex, pageSize } = table.getState().pagination
          const nextFocusedCell = getArrowKeyFocusTarget({
            key: event.key,
            rowIndex: row.index,
            columnId: column.id,
            datasets,
            rowsOnPage: Math.min(pageSize, times.length - pageIndex * pageSize),
          })

          if (nextFocusedCell === undefined) {
            return
          }

          event.preventDefault()
          setFocusedCell(nextFocusedCell)
        },
        onMouseDown: (event: React.MouseEvent<HTMLTableCellElement>) => {
          const settingData = getSettingData(settingsData, index)
          if (settingData === undefined) {
            return
          }

          const datasetIndex = getRowIndex(row, table.getState().pagination)
          const settingValue = settingData.value as TimeSeriesSettingValue
          const times: ISODateTime[] = Object.keys(settingValue) as ISODateTime[]
          const value = settingValue[times[datasetIndex]].value
          const startedOnFillHandle = isFillHandleTarget(event.target)

          if (startedOnFillHandle) {
            event.preventDefault()
          } else {
            setFocusedCell({ row: row.index, column: column.id })
          }

          handleMouseEvent(event, { rowIndex: row.index, value, mode: startedOnFillHandle ? 'fill' : 'select' })
        },
        onMouseOver: () => {
          if (mouseDownValue !== null) {
            setMouseOverIndex(row.index)
          }
        },
        onDrop: (event: React.MouseEvent<HTMLTableCellElement>) => {
          handleMouseEvent(event, null)
          setMouseOverIndex(null)
        },
        onMouseUp: (event: React.MouseEvent<HTMLTableCellElement>) => {
          const settingData = getSettingData(settingsData, index)
          if (settingData === undefined) {
            return
          }

          const datasetIndex = getRowIndex(row, table.getState().pagination)

          if (mouseDownValue === null || mouseDownValue.rowIndex === row.index) {
            selectRowByClick(datasetIndex, {
              extendRange: event.shiftKey,
              addToSelection: event.metaKey || event.ctrlKey,
            })
            setMouseDownValue(null)
            return
          }

          const settingValue = settingData.value as TimeSeriesSettingValue
          const settingTimes: ISODateTime[] = Object.keys(settingValue) as ISODateTime[]
          const selectedIndices = getRowIndexRange(mouseDownValue.rowIndex, row.index).map((rowIndexOnPage) =>
            getRowIndex(rowIndexOnPage, table.getState().pagination)
          )

          // A drag that started in the cell body only selects the range — copying the origin value
          // is what the fill handle is for.
          if (mouseDownValue.mode === 'fill') {
            updateTimeSeriesValues(
              getSettingValuesWithValueAtRows(settingValue, settingTimes, selectedIndices, mouseDownValue.value),
              index
            )
          }

          setSelectedRows(selectedIndices)
        },
      })
    },
    muiEditTextFieldProps: ({ index, returnId }: CreateMUITableBodyCellEditTextFieldPropsOptions) => {
      return ({ cell, column, row, table }: TablePropsFactoryFnProps): TextFieldProps => {
        const datasetIndex = getRowIndex(row, table.getState().pagination)
        const settingData = getSettingData(settingsData, index)
        const settingValue =
          typeof settingData?.value === 'object' ? (settingData.value as TimeSeriesSettingValue) : undefined
        const isBeingTypedInto = activeEdit?.rowIndex === datasetIndex && activeEdit.columnId === column.id

        return {
          variant: 'standard',
          type: 'number',
          value: isBeingTypedInto ? activeEdit.rawValue : (cell.getValue<string | number | null>() ?? ''),
          sx: {
            position: 'static',
            '& .MuiInputBase-input': {
              textAlign: 'right',
              fontFamily: 'monospace, monospace',
              fontSize: '120%',
              fontWeight: 600,
            },
          },
          inputRef:
            cellRefs.current[`${column.id}-${row.index}`] ||
            (cellRefs.current[`${column.id}-${row.index}`] = React.createRef()),
          inputProps: {
            onWheel: (event: React.WheelEvent<HTMLInputElement>) => {
              event.stopPropagation()
              event.currentTarget.blur()
            },
          },
          InputProps: {
            sx: { position: 'static' },
            endAdornment: (
              <Tooltip title={t('Drag to copy this value to the cells you drag over')} placement="top" arrow>
                <span className={styles.TimeSeriesSettingTable_FillHandle} {...fillHandleMarkerProps} />
              </Tooltip>
            ),
          },
          onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
            if (settingData === undefined || settingValue === undefined || column.id !== returnId) {
              return
            }

            setActiveEdit({ rowIndex: datasetIndex, columnId: column.id, rawValue: event.target.value })

            const valueAsNumber = event.target.value === '' ? null : Number(event.target.value)

            if (Number.isNaN(valueAsNumber)) {
              return
            }

            // Typing into a cell that is part of the selection writes to the whole selection — the
            // keyboard counterpart of dragging the fill handle over it.
            const rowsToUpdate = selectedRows.includes(datasetIndex) ? selectedRows : [datasetIndex]

            setSettingData({
              ...settingData,
              value: getSettingValuesWithValueAtRows(settingValue, times, rowsToUpdate, valueAsNumber),
            })
          },
          onBlur: () => {
            setActiveEdit(null)
            setMouseDownValue(null)
          },
          onPaste: (event: React.ClipboardEvent) => {
            if (settingValue === undefined) {
              return
            }

            event.preventDefault()

            updateTimeSeriesValues(
              getSettingValuesFromPaste({
                settingValue,
                times: Object.keys(settingValue) as ISODateTime[],
                clipboardText: event.clipboardData.getData('text'),
                targetRowIndex: datasetIndex,
                selectedRows,
              }),
              index
            )
          },
        }
      }
    },
  }
}
